<template>
  <div class="flex items-center space-x-2">
    <button
      type="button"
      class="flex items-center justify-center"
      @click="decrementDates"
    >
      <IconHeroiconsSolidChevronLeft />
    </button>
    <button
      type="button"
      class="bg-background block flex w-52 items-center justify-between rounded-full p-1 py-1 text-center"
      @click="showDialog"
    >
      <span
        class="mx-2 line-clamp-1 flex-1 text-center text-xs font-medium"
      >
        {{ label }}
      </span>
      <IconHeroiconsSolidChevronDown
        class="bg-background-fg/10 rounded-full"
      />
    </button>
    <button
      type="button"
      class="flex items-center justify-center"
      @click="() => incrementDates()"
    >
      <IconHeroiconsSolidChevronRight />
    </button>
  </div>
  <BaseDialog :is-open="isOpen" @close="onClose">
    <div class="p-2">
      <DateRangeSelect
        :model-value="modelValue"
        allow-range
        type="range"
        @update:model-value="onUpdate"
      />
      <div
        class="grid grid-cols-1 gap-2 p-2 sm:grid-cols-2"
      >
        <template
          v-for="quickAction in quickActions"
          :key="quickAction.label"
        >
          <button
            type="button"
            class="border-primary border-1 flex items-center justify-start space-x-2 rounded rounded-lg px-2 py-1"
            @click="quickAction.action"
          >
            <span>
              {{ quickAction.label }}
            </span>
          </button>
        </template>
      </div>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
import * as DateFns from "date-fns"

const props = defineProps<{
  modelValue: {
    start?: string
    end?: string
  }
}>()

const emit = defineEmits<{
  (
    event: "update:modelValue",
    params: typeof props.modelValue
  ): void
}>()

const modelValueAsDate = computed(() => ({
  start: props.modelValue.start
    ? DateFns.parse(
        props.modelValue.start,
        "yyyy-MM-dd",
        new Date()
      )
    : undefined,
  end: props.modelValue.end
    ? DateFns.parse(
        props.modelValue.end,
        "yyyy-MM-dd",
        new Date()
      )
    : undefined,
}))

const isOpen = ref(false)
const showDialog = () => {
  isOpen.value = true
}
const hideDialog = () => {
  isOpen.value = false
}

const quickActions = [
  {
    label: "Today",
    action: () => {
      emit("update:modelValue", {
        start: formatToModelValue(new Date()),
        end: formatToModelValue(new Date()),
      })
      hideDialog()
    },
  },
  {
    label: "This Week",
    action: () => {
      emit("update:modelValue", {
        start: formatToModelValue(
          DateFns.startOfISOWeek(new Date())
        ),
        end: formatToModelValue(
          DateFns.endOfISOWeek(new Date())
        ),
      })
      hideDialog()
    },
  },
  {
    label: "This Month",
    action: () => {
      emit("update:modelValue", {
        start: formatToModelValue(
          DateFns.startOfMonth(new Date())
        ),
        end: formatToModelValue(
          DateFns.endOfMonth(new Date())
        ),
      })
      hideDialog()
    },
  },
  {
    label: "This Year",
    action: () => {
      emit("update:modelValue", {
        start: formatToModelValue(
          DateFns.startOfYear(new Date())
        ),
        end: formatToModelValue(
          DateFns.endOfYear(new Date())
        ),
      })
      hideDialog()
    },
  },
  {
    label: "All Time",
    action: () => {
      emit("update:modelValue", {
        start: undefined,
        end: undefined,
      })
      hideDialog()
    },
  },
]

const label = computed(() => {
  if (
    !modelValueAsDate.value.start &&
    !modelValueAsDate.value.end
  ) {
    return "All Time"
  } else if (
    !modelValueAsDate.value.start &&
    modelValueAsDate.value.end
  ) {
    return `Before ${DateFns.format(
      modelValueAsDate.value.end,
      "do MMM yy"
    )}`
  } else if (
    modelValueAsDate.value.start &&
    !modelValueAsDate.value.end
  ) {
    return `After ${DateFns.format(
      modelValueAsDate.value.start,
      "do MMM yy"
    )}`
  } else if (
    modelValueAsDate.value.start &&
    modelValueAsDate.value.end
  ) {
    return getLabelForRange(
      modelValueAsDate.value.start,
      modelValueAsDate.value.end
    )
  }
  return ""
})

const isRangeInFullWeeks = (date1: Date, date2: Date) =>
  DateFns.isSameDay(date1, DateFns.startOfISOWeek(date1)) &&
  DateFns.isSameDay(date2, DateFns.endOfISOWeek(date2))

const isRangeInFullMonths = (date1: Date, date2: Date) =>
  DateFns.isSameDay(date1, DateFns.startOfMonth(date1)) &&
  DateFns.isSameDay(date2, DateFns.endOfMonth(date2))

const isRangeInFullYears = (date1: Date, date2: Date) =>
  DateFns.isSameDay(date1, DateFns.startOfYear(date1)) &&
  DateFns.isSameDay(date2, DateFns.endOfYear(date2))

const getLabelForRange = (date1: Date, date2: Date) => {
  if (isRangeInFullYears(date1, date2)) {
    if (
      DateFns.isThisYear(date1) &&
      DateFns.isThisYear(date2)
    ) {
      return "This Year"
    } else if (
      DateFns.isSameYear(
        date1,
        DateFns.addYears(new Date(), 1)
      ) &&
      DateFns.isSameYear(
        date2,
        DateFns.addYears(new Date(), 1)
      )
    ) {
      return "Next Year"
    } else if (
      DateFns.isSameYear(
        date1,
        DateFns.addYears(new Date(), -1)
      ) &&
      DateFns.isSameYear(
        date2,
        DateFns.addYears(new Date(), -1)
      )
    ) {
      return "Last Year"
    } else if (DateFns.isSameYear(date1, date2)) {
      return DateFns.format(date1, "yyyy")
    } else {
      return `${DateFns.format(
        date1,
        "yyyy"
      )} - ${DateFns.format(date2, "yyyy")}`
    }
  } else if (isRangeInFullMonths(date1, date2)) {
    // Date range is a month
    if (
      DateFns.isThisMonth(date1) &&
      DateFns.isThisMonth(date2)
    ) {
      return "This Month"
    } else if (
      DateFns.isSameMonth(
        date1,
        DateFns.addMonths(new Date(), 1)
      ) &&
      DateFns.isSameMonth(
        date2,
        DateFns.addMonths(new Date(), 1)
      )
    ) {
      return "Next Month"
    } else if (
      DateFns.isSameMonth(
        date1,
        DateFns.addMonths(new Date(), -1)
      ) &&
      DateFns.isSameMonth(
        date2,
        DateFns.addMonths(new Date(), -1)
      )
    ) {
      return "Last Month"
    } else if (DateFns.isSameMonth(date1, date2)) {
      return DateFns.format(date1, "MMM yyyy")
    } else {
      return `${DateFns.format(
        date1,
        "MMM yyyy"
      )} - ${DateFns.format(date2, "MMM yyyy")}`
    }
  } else if (isRangeInFullWeeks(date1, date2)) {
    // Date range is a week
    if (
      DateFns.isThisISOWeek(date1) &&
      DateFns.isThisISOWeek(date2)
    ) {
      return "This Week"
    } else if (
      DateFns.isSameISOWeek(
        date1,
        DateFns.addWeeks(new Date(), 1)
      ) &&
      DateFns.isSameISOWeek(
        date2,
        DateFns.addWeeks(new Date(), 1)
      )
    ) {
      return "Next Week"
    } else if (
      DateFns.isSameISOWeek(
        date1,
        DateFns.addWeeks(new Date(), -1)
      ) &&
      DateFns.isSameISOWeek(
        date2,
        DateFns.addWeeks(new Date(), -1)
      )
    ) {
      return "Last Week"
    }
  } else if (DateFns.isSameDay(date1, date2)) {
    if (DateFns.isToday(date1)) {
      return "Today"
    } else if (DateFns.isTomorrow(date1)) {
      return "Tomorrow"
    } else if (DateFns.isYesterday(date1)) {
      return "Yesterday"
    } else {
      return DateFns.format(date1, "do MMM yy")
    }
  } else {
    return `${DateFns.format(
      date1,
      "do MMM yy"
    )} - ${DateFns.format(date2, "do MMM yy")}`
  }
  return `${DateFns.format(
    date1,
    "do MMM yy"
  )} - ${DateFns.format(date2, "do MMM yy")}`
}
const onClose = () => {
  hideDialog()
}

const onUpdate = (value: typeof props.modelValue) => {
  emit("update:modelValue", value)
  hideDialog()
}

const incrementDates = (step = 1) => {
  if (
    !modelValueAsDate.value.start &&
    !modelValueAsDate.value.end
  ) {
    return
  } else if (
    !modelValueAsDate.value.end &&
    modelValueAsDate.value.start
  ) {
    emit("update:modelValue", {
      start: formatToModelValue(
        DateFns.addDays(modelValueAsDate.value.start, step)
      ),
      end: undefined,
    })
  } else if (
    !modelValueAsDate.value.start &&
    modelValueAsDate.value.end
  ) {
    emit("update:modelValue", {
      start: undefined,
      end: formatToModelValue(
        DateFns.addDays(modelValueAsDate.value.end, step)
      ),
    })
  } else if (
    modelValueAsDate.value.start &&
    modelValueAsDate.value.end
  ) {
    if (
      isRangeInFullYears(
        modelValueAsDate.value.start,
        modelValueAsDate.value.end
      )
    ) {
      emit("update:modelValue", {
        start: formatToModelValue(
          DateFns.addYears(
            modelValueAsDate.value.start,
            step
          )
        ),
        end: formatToModelValue(
          DateFns.endOfYear(
            DateFns.addYears(
              modelValueAsDate.value.end,
              step
            )
          )
        ),
      })
    } else if (
      isRangeInFullMonths(
        modelValueAsDate.value.start,
        modelValueAsDate.value.end
      )
    ) {
      emit("update:modelValue", {
        start: formatToModelValue(
          DateFns.addMonths(
            modelValueAsDate.value.start,
            step
          )
        ),
        end: formatToModelValue(
          DateFns.endOfMonth(
            DateFns.addMonths(
              modelValueAsDate.value.end,
              step
            )
          )
        ),
      })
    } else {
      const differenceInDays =
        DateFns.differenceInDays(
          modelValueAsDate.value.end,
          modelValueAsDate.value.start
        ) + 1
      emit("update:modelValue", {
        start: formatToModelValue(
          DateFns.addDays(
            modelValueAsDate.value.start,
            differenceInDays * step
          )
        ),
        end: formatToModelValue(
          DateFns.addDays(
            modelValueAsDate.value.end,
            differenceInDays * step
          )
        ),
      })
    }
  }
}

const formatToModelValue = (date: Date) =>
  DateFns.format(date, "yyyy-MM-dd")

const decrementDates = () => incrementDates(-1)
</script>
